import React, { useEffect, useState } from "react";
import AdminNavbar from "../../Pages/Navbar/AdminNavbar";
import Swal from "sweetalert2";
import BaseUrl from "../../ApiServices/BaseUrl";
import "./ManageAvailableJobs.css";

const predefinedRoutes = [
  "/alljobs",
  "/allcandidates",
  "/nurse",
  "/doctor",
  "/driver",
  "/cook",
  "/domestichelper",
  "/jobineurope"
];

const ManageAvailableJobs = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newImage, setNewImage] = useState(null);
  const [newImagePreview, setNewImagePreview] = useState("");
  const [routeMode, setRouteMode] = useState("predefined");
  const [selectedRoute, setSelectedRoute] = useState("/alljobs");
  const [customRoute, setCustomRoute] = useState("");
  const [sortOrder, setSortOrder] = useState(0);
  const [isActive, setIsActive] = useState(true);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BaseUrl}/api/available-jobs/admin`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Failed to fetch items");
      setItems(
        (data || []).map((item) => ({
          ...item,
          redirect_route: item.redirect_route || "/alljobs",
          sort_order: Number(item.sort_order) || 0,
          is_active: item.is_active === 1 || item.is_active === true
        }))
      );
    } catch (error) {
      Swal.fire("Error", error.message || "Failed to fetch data", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const getNewRouteValue = () => {
    if (routeMode === "custom") return customRoute.trim();
    return selectedRoute;
  };

  const handleCreate = async () => {
    const routeValue = getNewRouteValue();
    if (!newImage) {
      Swal.fire("Error", "Please upload an image", "error");
      return;
    }
    if (!routeValue) {
      Swal.fire("Error", "Please select or enter a redirect route", "error");
      return;
    }

    try {
      setSaving(true);
      const formData = new FormData();
      formData.append("image", newImage);
      formData.append("redirect_route", routeValue);
      formData.append("sort_order", String(sortOrder || 0));
      formData.append("is_active", isActive ? "1" : "0");

      const response = await fetch(`${BaseUrl}/api/available-jobs`, {
        method: "POST",
        body: formData
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Failed to create item");

      Swal.fire("Success", "Available job item added", "success");
      setNewImage(null);
      setNewImagePreview("");
      setRouteMode("predefined");
      setSelectedRoute("/alljobs");
      setCustomRoute("");
      setSortOrder(0);
      setIsActive(true);
      await fetchItems();
    } catch (error) {
      Swal.fire("Error", error.message || "Failed to save", "error");
    } finally {
      setSaving(false);
    }
  };

  const updateLocalItem = (id, key, value) => {
    setItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, [key]: value } : item))
    );
  };

  const handleUpdate = async (item) => {
    if (!item.redirect_route || !item.redirect_route.trim()) {
      Swal.fire("Error", "Redirect route is required", "error");
      return;
    }

    try {
      const response = await fetch(`${BaseUrl}/api/available-jobs/${item.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          redirect_route: item.redirect_route.trim(),
          sort_order: Number(item.sort_order) || 0,
          is_active: item.is_active ? 1 : 0
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Failed to update item");
      Swal.fire("Updated", "Item updated successfully", "success");
      await fetchItems();
    } catch (error) {
      Swal.fire("Error", error.message || "Failed to update item", "error");
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Delete this item?",
      text: "This cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#d33"
    });

    if (!result.isConfirmed) return;

    try {
      const response = await fetch(`${BaseUrl}/api/available-jobs/${id}`, {
        method: "DELETE"
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Failed to delete item");
      await fetchItems();
      Swal.fire("Deleted", "Item removed", "success");
    } catch (error) {
      Swal.fire("Error", error.message || "Failed to delete item", "error");
    }
  };

  return (
    <>
      <AdminNavbar />
      <div className="manage-avail-jobs-container">
        <div className="manage-avail-jobs-card">
          <h2>Manage Available Job Opportunities</h2>
          <p className="text-muted">
            Upload images and set redirect routes for the Home page section.
          </p>

          <div className="create-form">
            <div className="form-row">
              <label className="form-label">Upload Image</label>
              <input
                type="file"
                accept="image/*"
                className="form-control"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  setNewImage(file || null);
                  setNewImagePreview(file ? URL.createObjectURL(file) : "");
                }}
              />
            </div>

            <div className="form-row">
              <label className="form-label">Redirect Route</label>
              <div className="route-mode-wrap">
                <label>
                  <input
                    type="radio"
                    name="routeMode"
                    checked={routeMode === "predefined"}
                    onChange={() => setRouteMode("predefined")}
                  />{" "}
                  Select Route
                </label>
                <label>
                  <input
                    type="radio"
                    name="routeMode"
                    checked={routeMode === "custom"}
                    onChange={() => setRouteMode("custom")}
                  />{" "}
                  Custom Route / URL
                </label>
              </div>

              {routeMode === "predefined" ? (
                <select
                  className="form-control"
                  value={selectedRoute}
                  onChange={(e) => setSelectedRoute(e.target.value)}
                >
                  {predefinedRoutes.map((route) => (
                    <option key={route} value={route}>
                      {route}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  className="form-control"
                  placeholder="/alljobs or https://example.com"
                  value={customRoute}
                  onChange={(e) => setCustomRoute(e.target.value)}
                />
              )}
            </div>

            <div className="form-grid">
              <div className="form-row">
                <label className="form-label">Sort Order</label>
                <input
                  type="number"
                  className="form-control"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(parseInt(e.target.value || "0", 10))}
                />
              </div>
              <div className="form-row checkbox-row">
                <label>
                  <input
                    type="checkbox"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                  />{" "}
                  Active
                </label>
              </div>
            </div>

            {newImagePreview && (
              <div className="preview-wrap">
                <img src={newImagePreview} alt="Preview" className="preview-image" />
              </div>
            )}

            <button className="save-btn w-100 mt-3" disabled={saving} onClick={handleCreate}>
              {saving ? "Saving..." : "Add Item"}
            </button>
          </div>

          <hr />

          <h4>Existing Items</h4>
          {loading ? (
            <p>Loading...</p>
          ) : items.length === 0 ? (
            <p className="text-muted">No items added yet.</p>
          ) : (
            <div className="item-grid">
              {items.map((item) => (
                <div className="item-card" key={item.id}>
                  <img src={item.image_url} alt="Available Job" className="item-image" />

                  <label className="form-label mt-2">Route</label>
                  <input
                    type="text"
                    className="form-control"
                    value={item.redirect_route}
                    onChange={(e) => updateLocalItem(item.id, "redirect_route", e.target.value)}
                  />

                  <label className="form-label mt-2">Sort Order</label>
                  <input
                    type="number"
                    className="form-control"
                    value={item.sort_order}
                    onChange={(e) =>
                      updateLocalItem(
                        item.id,
                        "sort_order",
                        parseInt(e.target.value || "0", 10)
                      )
                    }
                  />

                  <label className="mt-2">
                    <input
                      type="checkbox"
                      checked={!!item.is_active}
                      onChange={(e) => updateLocalItem(item.id, "is_active", e.target.checked)}
                    />{" "}
                    Active
                  </label>

                  <div className="item-actions">
                    <button className="btn btn-primary btn-sm" onClick={() => handleUpdate(item)}>
                      Save
                    </button>
                    <button className="btn btn-danger btn-sm" onClick={() => handleDelete(item.id)}>
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ManageAvailableJobs;
